import React, { useContext, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import SignListContext, {
    vListKey,
    vTableColumn,
} from "../Context/SignListContext";
import MessageContext from "../Context/MessageContext";
import PageNumber from "../component/PageNumber";
import {
    IUserCommentInput,
    DoctorSaveCommentAPI,
    vSignListOutput,
    ListType,
} from "../utils/GeneBoxAPI";
import LoginInfoContext from "../Context/LoginInfoContext";
import GeneList from "../component/GeneList";

interface url {
    from: string;
    type: string;
}

const SignList = () => {
    const history = useHistory();
    const location = useLocation();
    const { type: SystemType }: url = useParams();

    const pageFrom = location.pathname.split("/")[1];
    const signer =
        pageFrom === "doctor_sign"
            ? "doctor"
            : pageFrom === "pmc_sign"
            ? "pmc"
            : undefined;

    const {
        vSignContextData,
        iSortNow,
        nowSortType,
        iNowPage,
        bQueryNow,
        sortSignData,
        changePage,
        getSignListData,
    } = useContext(SignListContext);

    const { alertProps, setAlertProps, setIsAlert } =
        useContext(MessageContext);
    const { errorProcess, setbLoading } = useContext(LoginInfoContext);
    const [checkedItem, setCheckedItem] = useState<IUserCommentInput[]>([]);
    const [bCheckAll, setbCheckAll] = useState(false);
    const iItemInPage = 10;
    const iMaxPage = Math.ceil(vSignContextData.length / iItemInPage);
    const iStartItem = (iNowPage - 1) * iItemInPage;
    const iEndItem = iNowPage * iItemInPage - 1;
    const currentPageItems = vSignContextData.slice(iStartItem, iEndItem + 1);

    const buildCheckedKey = (uid: string, report_version?: string) =>
        `${uid}_${String(report_version ?? "")}`;

    // click sort th
    const clickSortTh = (
        key_name: string,
        event: React.MouseEvent<HTMLTableHeaderCellElement>
    ) => {
        sortSignData(key_name, event);
        setCheckedItem([]);
        setbCheckAll(false);
    };

    // add select
    const addCheckedItem = (vSignData: vSignListOutput) => {
        const { uid, report_version } = vSignData;
        const currentKey = buildCheckedKey(uid, report_version);
        setCheckedItem((prevCheckedItem) => {
            const isChecked = prevCheckedItem.some(
                (item) =>
                    buildCheckedKey(item.uid, item.report_version) ===
                    currentKey
            );
            const newCheckedItem = isChecked
                ? prevCheckedItem.filter(
                      (item) =>
                          buildCheckedKey(item.uid, item.report_version) !==
                          currentKey
                  )
                : [...prevCheckedItem, { uid, comment: "", report_version }];
            const currentPageKeySet = new Set(
                currentPageItems.map((item) =>
                    buildCheckedKey(item.uid, item.report_version)
                )
            );
            const checkedInPageCount = newCheckedItem.filter((item) =>
                currentPageKeySet.has(
                    buildCheckedKey(item.uid, item.report_version)
                )
            ).length;
            setbCheckAll(
                currentPageItems.length > 0 &&
                    checkedInPageCount === currentPageItems.length
            );
            return newCheckedItem;
        });
    };

    // add all
    const addAllCheckedItem = (vChecked: boolean) => {
        setbCheckAll(vChecked);
        if (vChecked) {
            const newCheckedItem = currentPageItems.map((vSign) => ({
                uid: vSign.uid,
                comment: "",
                report_version: vSign.report_version,
            }));
            setCheckedItem(newCheckedItem);
        } else {
            setCheckedItem([]);
        }
    };

    // change page
    const changeSignPage = (page: number) => {
        changePage(page);
        setCheckedItem([]);
        setbCheckAll(false);
    };

    // click sign btn
    const clickSignReport = () => {
        if (checkedItem.length === 0) {
            // show alert
            setAlertProps({
                ...alertProps,
                title: "請勾選欲簽核名單",
                sub_title: "名單不能為空",
                iMsg: 2,
            });
            setIsAlert(true);
            return;
        }
        // axios 送出簽核
        setbLoading(true);
        Promise.allSettled(
            checkedItem.map((item) =>
                DoctorSaveCommentAPI(SystemType, [item], signer)
            )
        )
            .then((results) => {
                const successCount = results.filter(
                    (result) =>
                        result.status === "fulfilled" &&
                        (result.value.respCode === 200 ||
                            result.value.respCode === 201)
                ).length;
                const failCount = checkedItem.length - successCount;

                if (failCount === 0) {
                    setAlertProps({
                        ...alertProps,
                        title: "簽核成功",
                        sub_title: `${successCount} 筆已簽核`,
                        iMsg: 0,
                    });
                } else {
                    setAlertProps({
                        ...alertProps,
                        title: "部分簽核失敗",
                        sub_title: `成功 ${successCount} 筆，失敗 ${failCount} 筆`,
                        iMsg: 2,
                    });
                }
                setIsAlert(true);
                setCheckedItem([]);
                getSignListData();
                setbCheckAll(false);
            })
            .catch(() => {
                errorProcess();
            })
            .finally(() => {
                setbLoading(false);
            });
    };

    const checkStatus = (vSignData: vSignListOutput) => {
        const currentKey = buildCheckedKey(
            vSignData.uid,
            vSignData.report_version
        );
        return checkedItem.some(
            (i: IUserCommentInput) =>
                buildCheckedKey(i.uid, i.report_version) === currentKey
        );
    };

    const clickReport = (vSignData: vSignListOutput) => {
        const { uid, report_version } = vSignData;
        history.push(
            `/report/${pageFrom}/${uid.toLocaleLowerCase()}/${report_version}`
        );
    };

    const clickList = (vSignData: vSignListOutput, sKey: string) => {
        switch (sKey) {
            case vTableColumn[6].key_name:
                addCheckedItem(vSignData);
                break;
            default:
                clickReport(vSignData);
                break;
        }
    };

    const GeneListProps = {
        iSortNow,
        nowSortType,
        vListData: vSignContextData,
        vDataIndex: Object.keys(vSignContextData).map((idx) => parseInt(idx)),
        sType: ListType.sign_list,
        vKey: vListKey,
        iStartItem,
        iEndItem,
        bQueryNow,
        clickSortTh,
        clickList,
        bCheckAll,
        addAllCheckedItem,
        checkStatus,
    };

    return (
        <div className="sign_container">
            <button
                className="button_style button_yellow"
                onClick={clickSignReport}
            >
                批次簽核
            </button>

            <div className="table_container">
                <GeneList {...GeneListProps} />
            </div>
            <PageNumber
                iMaxPage={iMaxPage}
                iNowPage={iNowPage}
                changePage={changeSignPage}
            />
        </div>
    );
};

export default SignList;
